/**
 * Analytics Utilities for Session Depth Tracking
 * Tracks user engagement with key features across the application
 */

export interface FeatureEngagement {
  featureName: string;
  timestamp: number;
  sessionId: string;
}

export interface SessionDepthMetrics {
  uniqueFeatures: number;
  featureList: string[];
  sessionDuration: number;
  engagementScore: number;
}

const SESSION_STORAGE_KEY = 'aegis360_session_features';
const SESSION_ID_KEY = 'aegis360_session_id';
const SESSION_START_KEY = 'aegis360_session_start';

// Generate or retrieve session ID
export const getSessionId = (): string => {
  try {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
      sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
    }
    return sessionId;
  } catch (error) {
    console.error('Error getting session ID:', error);
    return `fallback_${Date.now()}`;
  }
};

// Track feature engagement
export const trackFeature = (featureName: string): void => {
  try {
    const sessionId = getSessionId();
    const featuresAccessed = getSessionFeatures();
    
    // Only track if feature hasn't been accessed in this session
    if (!featuresAccessed.includes(featureName)) {
      const updatedFeatures = [...featuresAccessed, featureName];
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(updatedFeatures));
      
      // Fire analytics event for individual feature access
      if (typeof window !== 'undefined' && (window as any).analytics?.track) {
        (window as any).analytics.track('feature_accessed', {
          feature_name: featureName,
          session_id: sessionId,
          timestamp: Date.now(),
          total_features_accessed: updatedFeatures.length
        });
      }
    }
  } catch (error) {
    console.error('Error tracking feature:', error);
  }
};

// Get all features accessed in current session
export const getSessionFeatures = (): string[] => {
  try {
    const featuresJson = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return featuresJson ? JSON.parse(featuresJson) : [];
  } catch (error) {
    console.error('Error getting session features:', error);
    return [];
  }
};

// Calculate session depth metrics
export const getSessionDepthMetrics = (): SessionDepthMetrics => {
  try {
    const featureList = getSessionFeatures();
    const sessionStart = parseInt(sessionStorage.getItem(SESSION_START_KEY) || Date.now().toString());
    const sessionDuration = Date.now() - sessionStart;
    
    // Engagement score: 0-100 based on features accessed and time spent
    // Formula: (unique_features * 20) + min(time_in_minutes * 2, 40)
    const engagementScore = Math.min(
      (featureList.length * 20) + Math.min(Math.floor(sessionDuration / 60000) * 2, 40),
      100
    );
    
    return {
      uniqueFeatures: featureList.length,
      featureList,
      sessionDuration,
      engagementScore
    };
  } catch (error) {
    console.error('Error calculating session depth:', error);
    return {
      uniqueFeatures: 0,
      featureList: [],
      sessionDuration: 0,
      engagementScore: 0
    };
  }
};

// Fire comprehensive session depth analytics event
export const trackSessionDepth = (): void => {
  try {
    const metrics = getSessionDepthMetrics();
    const sessionId = getSessionId();
    
    if (typeof window !== 'undefined' && (window as any).analytics?.track) {
      (window as any).analytics.track('session_depth_tracked', {
        session_id: sessionId,
        unique_features: metrics.uniqueFeatures,
        feature_list: metrics.featureList,
        session_duration_ms: metrics.sessionDuration,
        session_duration_minutes: Math.floor(metrics.sessionDuration / 60000),
        engagement_score: metrics.engagementScore,
        timestamp: Date.now()
      });
    }
    
    console.log('Session Depth Metrics:', metrics);
  } catch (error) {
    console.error('Error tracking session depth:', error);
  }
};

// Track when user completes onboarding
export const trackOnboardingComplete = (): void => {
  try {
    trackSessionDepth();
    
    if (typeof window !== 'undefined' && (window as any).analytics?.track) {
      (window as any).analytics.track('onboarding_completed', {
        session_id: getSessionId(),
        features_explored_during_onboarding: getSessionFeatures().length,
        timestamp: Date.now()
      });
    }
  } catch (error) {
    console.error('Error tracking onboarding completion:', error);
  }
};

// Reset session (useful for testing)
export const resetSession = (): void => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_START_KEY);
  } catch (error) {
    console.error('Error resetting session:', error);
  }
};

// Feature name constants for consistency
export const FEATURES = {
  AI_DEMO: 'ai_demo_viewed',
  AI_REAL_UPLOAD: 'ai_real_upload',
  DASHBOARD: 'dashboard_accessed',
  SETTINGS: 'settings_visited',
  REPORTS: 'reports_viewed',
  CRM: 'crm_accessed',
  QUOTES: 'quotes_viewed',
  APPOINTMENTS: 'appointments_managed',
  ANALYTICS: 'analytics_viewed',
  PREVENTION_CALENDAR: 'prevention_calendar_viewed',
  IOT_SENSORS: 'iot_sensors_accessed',
  COMPLIANCE: 'compliance_checked',
  PRICING_ADMIN: 'pricing_admin_accessed',
  BACKEND_ADMIN: 'backend_admin_accessed',
  MOBILE_SCANNER: 'mobile_scanner_used',
  AR_PEST_LENS: 'ar_pest_lens_used'
} as const;
